import React from 'react';
import { Progress } from 'reactstrap';
import StatusIcon from '../StatusIcon';
import { formatBytes } from '../DiskUsage';
import { isMediablockConnected } from '../../utils/mediablock';
import './MediablockStatusBar.scss';

const GIB = 1073741824;

const relativeTime = (date) => {
    if (!date) return null;
    const minutes = Math.round((Date.now() - new Date(date).getTime()) / 60000);
    if (isNaN(minutes) || minutes < 0) return null;
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} h ago`;
    return `${Math.round(hours / 24)} d ago`;
};

// The lifecycle chips, in display order. `state` is the value the content list filters on.
const STATE_CHIPS = [
    { state: 'transferring', count: 'transferring', tone: 'text-primary', icon: 'fa-arrow-down-long',
        label: n => `${n} transferring`, title: 'Transferring to the Mediablock' },
    { state: 'pending-deletion', count: 'pendingDeletion', tone: 'text-warning', icon: 'fa-trash',
        label: n => `${n} pending deletion`, title: 'Marked for deletion, waiting for the cleanup job' },
    // Removals the cleanup job has given up on. Kept out of the amber count above: they never
    // resolve on their own and would otherwise sit there unnoticed.
    { state: 'removal-failed', count: 'deletionFailed', tone: 'text-danger', icon: 'fa-exclamation-circle',
        label: n => `${n} removal${n === 1 ? '' : 's'} failed`, title: 'Removal failed — the cleanup job is no longer retrying' },
];

// Compact header for the content view: connection state, storage headroom and what is
// currently sitting on (or moving to) the Mediablock. With `onToggleState` the lifecycle chips
// double as filters on the list below; an active one stays visible even once its count is 0, so
// the filter can always be switched off from where it was switched on.
const MediablockStatusBar = ({ mediablock, stats = {}, lastSynced, activeStates = [], onToggleState }) => {
    const settings = mediablock || {};
    const isConnected = isMediablockConnected(settings);

    // Capacity and used space are reported in GB by the on-site daemon (see DiskUsage).
    const capacity = (settings.capacity || 0) * GIB;
    const used = (settings.usedDiskSpace || 0) * GIB;
    const free = Math.max(capacity - used, 0);
    const percentUsed = capacity ? (used / capacity) * 100 : null;

    let usageColor = 'success';
    if (percentUsed >= 80) usageColor = 'warning';
    if (percentUsed >= 90) usageColor = 'danger';

    const synced = relativeTime(lastSynced);
    const chips = STATE_CHIPS.filter(chip => (stats[chip.count] || 0) > 0 || activeStates.includes(chip.state));

    return (
        <div className={`mediablock-bar ${isConnected ? '' : 'is-offline'}`}>
            <div className="mbar-identity">
                <span className="fas fa-cube" />
                <div className="mbar-item">
                    <span className="mbar-label">Mediablock</span>
                    <span className="mbar-value text-truncate">
                        {settings.productName || settings.serialNumber || 'Unknown model'}
                    </span>
                </div>
            </div>

            <div className="mbar-divider" />

            <div className="mbar-item">
                <span className="mbar-label">Status</span>
                <span className="mbar-value">
                    <StatusIcon type="mediablockStatus" value={settings.status} showLabel />
                </span>
            </div>

            <div className="mbar-divider" />

            <div className="mbar-item mbar-storage">
                <div className="d-flex justify-content-between align-items-baseline gap-3">
                    <span className="mbar-label">Storage</span>
                    {percentUsed !== null && (
                        <span className={`mbar-usage text-${usageColor}`}>
                            {percentUsed.toFixed(0)}% used
                        </span>
                    )}
                </div>
                {percentUsed === null ? (
                    <span className="mbar-value text-secondary">No storage data reported</span>
                ) : (
                    <>
                        <Progress
                            color={usageColor}
                            value={percentUsed}
                            className="mbar-bar"
                            aria-label="Mediablock storage usage"
                        />
                        <span className="mbar-value">
                            <strong>{formatBytes(free)} free</strong>
                            <span className="mbar-muted"> · {formatBytes(used)} of {formatBytes(capacity)}</span>
                        </span>
                    </>
                )}
            </div>

            <div className="mbar-divider" />

            <div className="mbar-item">
                <span className="mbar-label">Content</span>
                <span className="mbar-value">
                    {stats.cpls || 0} DCP{stats.cpls === 1 ? '' : 's'}
                    {stats.total > stats.cpls && (
                        <span className="mbar-muted"> · {stats.total} files</span>
                    )}
                </span>
            </div>

            {(chips.length > 0 || synced) && (
                <div className="mbar-flags">
                    {chips.map(chip => {
                        const active = activeStates.includes(chip.state);
                        const content = (
                            <>
                                <span className={`fas ${active ? 'fa-toggle-on' : chip.icon} me-2`} />
                                {chip.label(stats[chip.count] || 0)}
                            </>
                        );
                        return onToggleState ? (
                            <button
                                type="button"
                                key={chip.state}
                                className={`mbar-chip ${chip.tone}${active ? ' is-active' : ''}`}
                                title={`${chip.title} — click to ${active ? 'stop filtering' : 'filter the list'}`}
                                aria-pressed={active}
                                onClick={() => onToggleState(chip.state)}
                            >
                                {content}
                            </button>
                        ) : (
                            <span key={chip.state} className={`mbar-chip ${chip.tone}`} title={chip.title}>{content}</span>
                        );
                    })}
                    {synced && <span className="mbar-muted mbar-synced">synced {synced}</span>}
                </div>
            )}
        </div>
    );
};

export default MediablockStatusBar;
